import { useHandler } from "@redotech/react-util/hook";
import { REDO_REFERRAL_ID_PARAM } from "@redotech/redo-model/customer-portal";
import { createContext, ReactNode, useState } from "react";

export const REFERRAL_ID_KEY = "redo.referral_id";

/**
 * Stores the referral ID in local storage
 * @param referralId The ID of the referring agent
 */
export function storeReferralId(referralId: string): void {
  try {
    localStorage.setItem(REFERRAL_ID_KEY, referralId);
  } catch (error) {
    console.error("Error storing referral ID in local storage:", error);
  }
}

/**
 * Retrieves the referral ID from local storage
 * @returns The referral ID or null if not found
 */
export function getReferralId(): string | null {
  try {
    return localStorage.getItem(REFERRAL_ID_KEY);
  } catch (error) {
    console.error("Error retrieving referral ID from local storage:", error);
    return null;
  }
}

/**
 * Clears the referral ID from local storage
 * This should be called after a return is created using the referral ID
 */
export function clearReferralId(): void {
  try {
    localStorage.removeItem(REFERRAL_ID_KEY);
  } catch (error) {
    console.error("Error clearing referral ID from local storage:", error);
  }
}

/**
 * Checks the URL for a referral ID parameter and stores it if found
 * @param searchParams URL search parameters
 */
export function checkAndStoreReferralFromUrl(
  searchParams: URLSearchParams,
): void {
  const referralId = searchParams.get(REDO_REFERRAL_ID_PARAM);
  if (referralId) {
    storeReferralId(referralId);
  }
}

export interface ReferralContextType {
  referralId: string | null;
  setReferralId: (id: string) => void;
  clearReferralId: () => void;
}

export const ReferralContext = createContext<ReferralContextType | undefined>(
  undefined,
);

export const ReferralProvider = ({ children }: { children: ReactNode }) => {
  const [referralId, setReferralIdState] = useState<string | null>(
    getReferralId(),
  );

  const setReferralId = useHandler((id: string) => {
    storeReferralId(id);
    setReferralIdState(id);
  });

  const clearReferralIdHandler = useHandler(() => {
    clearReferralId();
    setReferralIdState(null);
  });

  const contextValue: ReferralContextType = {
    referralId,
    setReferralId,
    clearReferralId: clearReferralIdHandler,
  };

  return (
    <ReferralContext.Provider value={contextValue}>
      {children}
    </ReferralContext.Provider>
  );
};
